'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { ZoomIn, ArrowRight } from 'lucide-react';
import { useSiteData } from '@/lib/dataCache';
import OptimizedImage from '@/components/ui/OptimizedImage';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

type ApiGalleryImage = {
  id: string;
  title?: string | null;
  description?: string | null;
  url: string;
  category: string;
  width?: number | null;
  height?: number | null;
};

const GRADIENTS = [
  'linear-gradient(135deg, #6C63FF 0%, #8D84FF 50%, #FF6584 100%)',
  'linear-gradient(135deg, #FF6584 0%, #f59e0b 100%)',
  'linear-gradient(135deg, #06b6d4 0%, #6C63FF 100%)',
  'linear-gradient(135deg, #22c55e 0%, #06b6d4 100%)',
  'linear-gradient(135deg, #a855f7 0%, #FF6584 100%)',
  'linear-gradient(135deg, #f59e0b 0%, #FF6584 50%, #a855f7 100%)',
];

const RATIOS: Array<'landscape' | 'portrait' | 'square'> = [
  'landscape',
  'portrait',
  'square',
  'portrait',
  'landscape',
  'square',
];

function pickRatio(img: ApiGalleryImage, index: number): 'landscape' | 'portrait' | 'square' {
  if (img.width && img.height) {
    const r = img.width / img.height;
    if (r > 1.2) return 'landscape';
    if (r < 0.85) return 'portrait';
    return 'square';
  }
  return RATIOS[index % RATIOS.length];
}

export default function Gallery() {
  const items = useSiteData<ApiGalleryImage[]>('gallery', '/api/gallery', []);

  const display = items.slice(0, 6);

  return (
    <section id="gallery" className="gallery-section">
      <div className="gallery-blob gallery-blob-1" />
      <div className="gallery-blob gallery-blob-2" />

      <div className="section-container">
        <motion.div
          className="section-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <span className="section-label">Gallery</span>
          <h2 className="section-title">Visual Showcase</h2>
          <p className="section-subtitle">
            A curated selection of interface design, product shots and creative
            experiments from recent work.
          </p>
        </motion.div>

        <div className="gallery-masonry">
          {display.length === 0 && (
            <div className="gallery-empty">No gallery items yet.</div>
          )}
          {display.map((item, i) => {
            const ratio = pickRatio(item, i);
            const gradient = GRADIENTS[i % GRADIENTS.length];
            const title = item.title || 'Untitled';
            return (
              <motion.div
                key={item.id}
                className={`gallery-item gallery-item-${ratio}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: 'easeOut' }}
              >
                <div
                  className="gallery-placeholder"
                  style={{ background: gradient }}
                >
                  <OptimizedImage
                    src={item.url}
                    alt={title}
                    className="gallery-tile-img"
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    loading="lazy"
                  />
                  <div className="gallery-placeholder-shine" />
                </div>

                <div className="gallery-overlay">
                  <div className="gallery-overlay-top">
                    <span className="gallery-category">{item.category}</span>
                  </div>
                  <div className="gallery-overlay-bottom">
                    <h4 className="gallery-title">{title}</h4>
                    <div className="gallery-zoom">
                      <ZoomIn size={18} />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          className="gallery-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Link href="/gallery" className="btn btn-primary gallery-cta-btn">
            View Full Gallery
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
